import { AxnAdminUserPropertyBundleTypes } from "@enums/axn-admin-user-property-bundle-types";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { AxnAdminAvailableUserProperty } from "@idw-types/data/axn-admin-available-user-property";
import { AxnAdminUser, AxnAdminUserOrganizationProperty, FormattedAxnAdminUser } from "@idw-types/data/axn-admin-user";
import { AxnAdminUserPropertyBundle } from "@idw-types/data/axn-admin-user-property-bundle";
import { AxnAdminUserProperty } from "@idw-types/data/axn-admin-user/axn-admin-user-property";
import { AxnAdminUserRpProperty } from "@idw-types/data/axn-admin-user/axn-admin-user-rp-property";
import { OrganizationUserRoleInfo } from "@idw-types/data/axn-admin-user/organization-user-role-info";
import { AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { IDWSession } from "@idw-types/data/idw-session";
import { toObject } from "@utils/object-util";
import { sortStrings } from "@utils/string-util";
import { RelyingPartyRole } from "@idw-types/common";
import { resources } from "@constants/hardcoded-strings";

const shouldFilterUser = (properties: Array<AxnAdminUserProperty | AxnAdminUserOrganizationProperty | AxnAdminUserRpProperty>, newAxnAdminUserPropertyBundlesMap: { [id: string]: AxnAdminUserPropertyBundle }) => {
    return properties.some(property => newAxnAdminUserPropertyBundlesMap[property.propertyName] !== undefined);
}

export const getAxnAdminUserPropertyBundlesMap = (axnAdminUserPropertyBundles: Array<AxnAdminUserPropertyBundle>, organizationUsersRoles: Array<SelectOption>) => {
    const organizationUsersRoleIdsMap = toObject<SelectOption>(organizationUsersRoles, "value");

    const newAxnAdminUserPropertyBundles = axnAdminUserPropertyBundles.filter(bundle => {
        return organizationUsersRoleIdsMap[bundle.id] !== undefined;
    });

    return toObject<AxnAdminUserPropertyBundle>(newAxnAdminUserPropertyBundles, "shortName");
}

export const getAxnAdminFilteredUsers = (axnAdminUsers: Array<FormattedAxnAdminUser>, axnAdminUserPropertyBundles: Array<AxnAdminUserPropertyBundle>, organizationUsersRoles: Array<SelectOption>) => {
    if (axnAdminUserPropertyBundles && organizationUsersRoles.length && axnAdminUsers) {
        const newAxnAdminUserPropertyBundlesMap = getAxnAdminUserPropertyBundlesMap(axnAdminUserPropertyBundles, organizationUsersRoles);

        const filteredUsers = axnAdminUsers.filter(user => {
            let isFilteredUser = false;
            if (user.axnAdminUserOrganizationProperties?.length) {
                isFilteredUser = shouldFilterUser(user.axnAdminUserOrganizationProperties, newAxnAdminUserPropertyBundlesMap);
            }

            if (user.axnAdminUserProperties?.length) {
                isFilteredUser = isFilteredUser || shouldFilterUser(user.axnAdminUserProperties, newAxnAdminUserPropertyBundlesMap);
            }

            if (user.axnAdminUserRPProperties?.length) {
                isFilteredUser = isFilteredUser || shouldFilterUser(user.axnAdminUserRPProperties, newAxnAdminUserPropertyBundlesMap);
            }

            return isFilteredUser;
        });

        return filteredUsers;
    } else {
        return (axnAdminUsers || []);
    }
}

const rpToSelectOption = (relyingParty: AxnRelyingParty) => {
    return {
        label: relyingParty.name,
        value: relyingParty.id
    }
};

const areAllValuesInBothArrays = (
    array1: Array<string>,
    array2: Array<string>,
    valuesToExcludeFromArray2: Array<string>
) => {
    //check that every key exists inside of both array
    return (
        array1.every(value => array2.includes(value)) &&
        array2
            .filter(value => !valuesToExcludeFromArray2.includes(value))
            .every(value => array1.includes(value))
    );
};

const getRelyingPartyRoles = (
    axnAdminUserPropertyBundles: Array<AxnAdminUserPropertyBundle>,
    axnAdminUserRPProperties: Array<AxnAdminUserRpProperty>
): Array<AxnAdminUserRpProperty> => {
    const rpBundles = axnAdminUserPropertyBundles.filter(bundle => bundle.type === AxnAdminUserPropertyBundleTypes.RP);
    const rpBundleShortNames = rpBundles.map(rpBundle => rpBundle.shortName);
    // get all properties that match role shortnames and are true
    const relyingPartyRoles = axnAdminUserRPProperties.filter(property =>
        rpBundleShortNames.includes(property.propertyName) &&
        isStringTrue(property.propertyValue)
    );

    return relyingPartyRoles;
};

export const getRolesForUserWithIsCustomFlag = (
    user: AxnAdminUser,
    axnRelyingParties: Array<AxnRelyingParty>,
    axnAdminUserPropertyBundles: Array<AxnAdminUserPropertyBundle>,
    axnAdminAvailableUserOrganizationProperties: Array<AxnAdminAvailableUserProperty>,
    axnAdminAvailableUserRPProperties: Array<AxnAdminAvailableUserProperty>
): OrganizationUserRoleInfo => {
    const roleInfo: OrganizationUserRoleInfo = {
        organizationRole: null,
        relyingPartyRoles: [],
        adminProperty: null,
        isCustomOrgRole: false
    };

    if (user.axnAdminUserOrganizationProperties?.length && !user.axnAdminUserRPProperties?.length) {
        const mappedOrgProperties = user.axnAdminUserOrganizationProperties.map(prop => prop.propertyName);

        const bundleShortNames = axnAdminUserPropertyBundles.map(bundle => bundle.shortName);

        const bundle = axnAdminUserPropertyBundles
            .filter(upb => upb.type === AxnAdminUserPropertyBundleTypes.ORGANIZATION)
            .find(upb => {
                const upbPropertyIds = upb.userOrganizationProperties.map(property => property.availablePropertyID);
                const bundlePropertyNames = axnAdminAvailableUserOrganizationProperties
                    .filter(prop => upbPropertyIds.includes(prop.id))
                    .map(prop => prop.shortName);

                return areAllValuesInBothArrays(bundlePropertyNames, mappedOrgProperties, bundleShortNames);
            });

        roleInfo.organizationRole = bundle;
        roleInfo.isCustomOrgRole = !bundle;
    }

    if (user.axnAdminUserProperties?.length) {
        const adminProperty = user.axnAdminUserProperties.find(property => property.propertyName === "admin");
        if (adminProperty && isStringTrue(adminProperty.propertyValue)) {
            roleInfo.adminProperty = adminProperty;
        }
    }

    if (user.axnAdminUserRPProperties?.length) {
        const relyingPartyRoles = getRelyingPartyRoles(axnAdminUserPropertyBundles, user.axnAdminUserRPProperties);

        relyingPartyRoles.forEach(rpr => {
            const relyingParty = axnRelyingParties.find(rp => rp.id === rpr.relyingPartyID);
            const mappedRpProperties = user.axnAdminUserRPProperties
                .filter(prop => prop.relyingPartyID === rpr.relyingPartyID)
                .map(prop => prop.propertyName);

            if (relyingParty) {
                const bundleShortNames = axnAdminUserPropertyBundles.map(bundle => bundle.shortName);

                const bundle = axnAdminUserPropertyBundles
                    .filter(upb => upb.type === AxnAdminUserPropertyBundleTypes.RP)
                    .find(upb => {
                        //get all rp properties in the bundle
                        const upbPropertyIds = upb.userRPProperties.map(property => property.availablePropertyID);
                        const bundlePropertyNames = axnAdminAvailableUserRPProperties
                            .filter(prop => upbPropertyIds.includes(prop.id))
                            .map(prop => prop.shortName);

                        return areAllValuesInBothArrays(bundlePropertyNames, mappedRpProperties, bundleShortNames);
                    });

                roleInfo.relyingPartyRoles.push({
                    relyingParty,
                    bundle: bundle
                });
            }
        });
    }

    return roleInfo;
};


export const getOrganizationRoleFromUser = (
    user: AxnAdminUser,
    axnRelyingParties: Array<AxnRelyingParty>,
    axnAdminUserPropertyBundles: Array<AxnAdminUserPropertyBundle>
): OrganizationUserRoleInfo => {
    const roleInfo: OrganizationUserRoleInfo = {
        organizationRole: null,
        relyingPartyRoles: [],
        adminProperty: null,
        isCustomOrgRole: false
    };

    if (user.axnAdminUserOrganizationProperties?.length) {
        const mappedOrgProperties = user.axnAdminUserOrganizationProperties.map(prop => prop.propertyName);
        const roleProperty = axnAdminUserPropertyBundles.find(bundle => mappedOrgProperties.includes(bundle.shortName))
        if (roleProperty) {
            roleInfo.organizationRole = roleProperty;
        }
    }

    if (user.axnAdminUserProperties?.length) {
        const adminProperty = user.axnAdminUserProperties.find(property => property.propertyName === "admin");
        if (adminProperty && isStringTrue(adminProperty.propertyValue)) {
            roleInfo.adminProperty = adminProperty;
        }
    }

    if (user.axnAdminUserRPProperties?.length) {
        const relyingPartyRoles = getRelyingPartyRoles(axnAdminUserPropertyBundles, user.axnAdminUserRPProperties);

        relyingPartyRoles.forEach(rpr => {
            const relyingParty = axnRelyingParties.find(rp => rp.id === rpr.relyingPartyID);
            const bundle = axnAdminUserPropertyBundles.find(upb => upb.shortName === rpr.propertyName);

            if (relyingParty && bundle) {
                roleInfo.relyingPartyRoles.push({
                    relyingParty,
                    bundle
                });
            }
        });
    }

    return roleInfo;
};

export const getRelyingPartyOptions = (axnRelyingParties: Array<AxnRelyingParty>, organizationID: string) => {
    const shouldGetAllRelyingParties = organizationID === null;

    return axnRelyingParties.reduce((result, relyingParty) => {
        if (!shouldGetAllRelyingParties) {
            if (relyingParty.companyID === organizationID) {
                result.push(rpToSelectOption(relyingParty));
            }
        } else {
            result.push(rpToSelectOption(relyingParty));
        }
        return result;
    }, []).sort((a, b) => sortStrings(a.label, b.label));
}

export const getAxnAdminUserPropertyBundleProperties = (
    newOrganizationRole: AxnAdminUserPropertyBundle,
    newRelyingPartyRoles: Array<RelyingPartyRole>,
    axnAdminAvailableUserOrganizationProperties: Array<AxnAdminAvailableUserProperty>,
    axnAdminAvailableUserRPProperties: Array<AxnAdminAvailableUserProperty>,
    axnAdminUser: AxnAdminUser
) => {
    let userOrgNewProperties: Array<AxnAdminUserOrganizationProperty> = [];
    let userRpNewProperties: Array<AxnAdminUserRpProperty> = [];

    if (newOrganizationRole) {
        userOrgNewProperties = getOrgUserProperties(
            newOrganizationRole,
            axnAdminAvailableUserOrganizationProperties,
            axnAdminUser,
            true
        );
    } else {
        userRpNewProperties = getRelyingPartyProperties(
            newRelyingPartyRoles,
            axnAdminAvailableUserRPProperties,
            axnAdminUser,
            true
        );

        if (newRelyingPartyRoles.length > 0) {
            userOrgNewProperties = getOrgUserProperties(
                newRelyingPartyRoles[0].bundle,
                axnAdminAvailableUserOrganizationProperties,
                axnAdminUser,
                false
            );
        }
    }

    return {
        axnAdminUserOrganizationProperties: userOrgNewProperties,
        axnAdminUserRPProperties: userRpNewProperties
    }
};

const getOrgUserProperties = (
    newAxnAdminUserPropertyBundle: AxnAdminUserPropertyBundle,
    axnAdminAvailableUserOrganizationProperties: Array<AxnAdminAvailableUserProperty>,
    axnAdminUser: AxnAdminUser,
    isOrgRole: boolean
) => {
    const newProperties: Array<{
        propertyName: string;
        propertyValue: string;
        organizationID: string;
        userID: string;
        id: string;
    }> = [];

    newAxnAdminUserPropertyBundle.userOrganizationProperties.forEach(uop => {
        const property = axnAdminAvailableUserOrganizationProperties.find(auop => auop.id === uop.availablePropertyID);
        if (property) {
            newProperties.push({
                propertyName: property.shortName,
                propertyValue: uop.value,
                organizationID: axnAdminUser.organizationID,
                userID: axnAdminUser.id,
                id: undefined
            });
        }
    });

    if (isOrgRole) {
        newProperties.push({
            propertyName: newAxnAdminUserPropertyBundle.shortName,
            propertyValue: "true",
            organizationID: axnAdminUser.organizationID,
            userID: axnAdminUser.id,
            id: undefined
        });
    }

    return newProperties;
}

const getRelyingPartyProperties = (
    newRelyingPartyRoles: Array<RelyingPartyRole>,
    axnAdminAvailableUserRPProperties: Array<AxnAdminAvailableUserProperty>,
    axnAdminUser: AxnAdminUser,
    isRelyingPartyRole: boolean
) => {
    const userRpNewProperties: Array<{
        propertyName: string;
        propertyValue: string;
        relyingPartyID: string;
        userID: string;
        id: string;
    }> = [];

    newRelyingPartyRoles.forEach(rpr => {
        rpr.bundle.userRPProperties.forEach(userRpProperty => {
            const property = axnAdminAvailableUserRPProperties.find(aurp => aurp.id === userRpProperty.availablePropertyID);

            if (property) {
                userRpNewProperties.push({
                    propertyName: property.shortName,
                    propertyValue: userRpProperty.value,
                    relyingPartyID: rpr.relyingParty.id,
                    userID: axnAdminUser.id,
                    id: undefined
                });
            }
        });

        if (isRelyingPartyRole) {
            userRpNewProperties.push({
                propertyName: rpr.bundle.shortName,
                propertyValue: "true",
                relyingPartyID: rpr.relyingParty.id,
                userID: axnAdminUser.id,
                id: undefined
            });
        }
    });

    return userRpNewProperties;
};

export const getAllowedRelyingPartiesForOrganizationId = (
    axnRelyingParties: Array<AxnRelyingParty>,
    organizationId: string
) => {
    if (!axnRelyingParties) {
        return [];
    }

    if (organizationId) {
        return axnRelyingParties.filter(rp => rp.companyID === organizationId);
    }

    return axnRelyingParties;
};

export const getAllowedAxnAdminUserPropertyBundles = (
    session: IDWSession,
    axnRelyingParties: Array<AxnRelyingParty>,
    axnAdminUserPropertyBundles: Array<AxnAdminUserPropertyBundle>
) => {
    if (session?.user && axnRelyingParties && axnAdminUserPropertyBundles) {
        const { organizationRole, adminProperty } = getOrganizationRoleFromUser(
            session.user,
            axnRelyingParties,
            axnAdminUserPropertyBundles
        );

        if (organizationRole || adminProperty) {
            return axnAdminUserPropertyBundles;
        } else {
            return axnAdminUserPropertyBundles.filter(bundle => bundle.type === AxnAdminUserPropertyBundleTypes.RP);
        }
    }

    return [];
};

export const isRelyingPartyUser = (userSession: AxnAdminUser, relyingParties: Array<AxnRelyingParty>,
    axnAdminUserPropertyBundles: Array<AxnAdminUserPropertyBundle>) => {
    const roleInfo = getOrganizationRoleFromUser(
        userSession,
        relyingParties,
        axnAdminUserPropertyBundles
    );

    return roleInfo.relyingPartyRoles.length > 0

}

export const organizationUserRolesToString = (roleInfo: OrganizationUserRoleInfo) => {
    const {
        organizationRole,
        relyingPartyRoles,
        adminProperty, isCustomOrgRole
    } = roleInfo;

    const CUSTOM_ROLE_KEY = 'Custom';
    const RELYING_PARTY_LABEL = "Relying Party";

    if (organizationRole) {
        return organizationRole.name;
    } else if (relyingPartyRoles.length > 0) {
        const idsMap: Record<string, string> = {};
        const roles = relyingPartyRoles.reduce((acc, role) => {
            if (role.bundle) {
                const { id: roleId, name } = role.bundle;

                if (!idsMap[roleId]) {
                    const roleName = name.replace(RELYING_PARTY_LABEL, "")
                    acc.push(roleName);
                    idsMap[roleId] = roleId;
                }
            } else if (!idsMap[CUSTOM_ROLE_KEY]) {
                const label = `${CUSTOM_ROLE_KEY} Rp User`;
                idsMap[CUSTOM_ROLE_KEY] = label;
                acc.push(label);
            }

            return acc;
        }, []);

        return roles[0] === `${CUSTOM_ROLE_KEY} Rp User` ? roles.join(", ") : `${RELYING_PARTY_LABEL} ${roles.join(", ")}`;
    } else if (adminProperty) {
        return "IDW Admin";
    } else if (isCustomOrgRole) {
        return `${CUSTOM_ROLE_KEY} Org User`;
    } else {
        return resources.common.notFound;
    }
}